import { useCallback, useEffect, useMemo, useState } from "react";

import { useActionHook } from "hooks";
import { useBulkUploadActions } from "store";
import {
  IBulkBatchReview,
  IBulkFileRow,
  IBulkMeasureCell,
  IBulkReviewFileEntry,
  IBulkReviewTag,
  IBulkValueRow
} from "store/types";
import { toastify } from "utils/tyostify";

const hasInvestorTarget = (source: Pick<IBulkReviewTag, "investor" | "investor_ids" | "all_investors">): boolean =>
  Boolean(source.investor) || (source.investor_ids?.length ?? 0) > 0 || source.all_investors;

const getBulkTagErrors = (tag: IBulkReviewTag, file: IBulkReviewFileEntry): string[] => {
  const errors: string[] = [];

  if (tag.value == null) errors.push("A value is required.");
  if (!tag.currency && !file.currency) errors.push("A currency is required.");
  if (!tag.effective_date && !file.effective_date) errors.push("An effective date is required.");

  if (tag.measure?.id == null) {
    errors.push("A measure must be assigned.");
  } else if (!tag.measure.is_global_value && !hasInvestorTarget(tag) && !hasInvestorTarget(file)) {
    errors.push("An investor must be assigned.");
  }

  return errors;
};

const isBulkTagComplete = (tag: IBulkReviewTag, file: IBulkReviewFileEntry): boolean =>
  getBulkTagErrors(tag, file).length === 0;

const investorLabel = (file: IBulkReviewFileEntry): string => {
  if (file.investor?.name) return file.investor.name;
  if (file.all_investors) return "All investors";
  return "—";
};

const tagInvestorLabel = (tag: IBulkReviewTag): string => {
  if (tag.investor?.name) return tag.investor.name;
  if (tag.all_investors) return "All investors";
  return "—";
};

const fileTypeOf = (file: IBulkReviewFileEntry): string => file.type || file.name.split(".").pop()?.toLowerCase() || "";

const buildMeasureCells = (file: IBulkReviewFileEntry): Record<string, IBulkMeasureCell> => {
  const cells: Record<string, IBulkMeasureCell> = {};
  file.tags.forEach((tag) => {
    const name = tag.measure?.name;
    if (!name) return;
    const existing = cells[name];
    if (existing) {
      existing.extraCount += 1;
      return;
    }
    cells[name] = { tagId: tag.id, value: tag.value, currency: tag.currency, extraCount: 0 };
  });
  return cells;
};

/** "Wide" row: one per file, measures pivoted into columns. */
const toFileRow = (file: IBulkReviewFileEntry): IBulkFileRow => ({
  fileId: file.id,
  fileName: file.name,
  fileType: fileTypeOf(file),
  documentType: file.document_type,
  status: file.file_status,
  dealName: file.deal?.name ?? "",
  dealId: file.deal?.id ?? null,
  investorName: investorLabel(file),
  investorId: file.investor?.id ?? null,
  allInvestors: file.all_investors,
  investorIds: file.investor_ids ?? [],
  splitByCommitment: file.split_by_commitment,
  entireCommitment: file.entire_commitment,
  currency: file.currency,
  effectiveDate: file.effective_date,
  addDate: file.created_at,
  isNew: file.is_new,
  hasAiTags: file.has_ai_tags,
  measures: buildMeasureCells(file)
});

/** "Long" rows: one per extracted measure value (tag) within a file. */
const toValueRows = (file: IBulkReviewFileEntry): IBulkValueRow[] =>
  file.tags.map((tag) => ({
    rowId: `${file.id}-${tag.id}`,
    tagId: tag.id,
    fileId: file.id,
    fileName: file.name,
    fileType: fileTypeOf(file),
    status: file.file_status,
    measureName: tag.measure?.name ?? "—",
    investorName: tagInvestorLabel(tag),
    dealId: file.deal?.id ?? null,
    investorId: tag.investor?.id ?? null,
    allInvestors: tag.all_investors,
    investorIds: tag.investor_ids ?? [],
    entireCommitment: tag.entire_commitment,
    value: tag.value,
    currency: tag.currency,
    effectiveDate: tag.effective_date,
    accountingDate: tag.accounting_date,
    round: tag.round,
    source: file.source,
    isNew: file.is_new,
    hasAiTags: tag.is_ai,
    isComplete: isBulkTagComplete(tag, file),
    validationErrors: getBulkTagErrors(tag, file)
  }));

export interface UseBulkReviewState {
  loading: boolean;
  review: IBulkBatchReview | undefined;
  rows: IBulkFileRow[];
  valueRows: IBulkValueRow[];
  measureColumns: string[];
  readOnly: boolean;
  updateFileRow: (fileId: number, patch: Partial<IBulkFileRow>) => void;
  refetch: () => void;
}

export const useBulkReviewState = (batchId: string): UseBulkReviewState => {
  const { getBulkUploadBatchReviewAction } = useBulkUploadActions();

  const {
    loading,
    data: payload,
    action: loadReview
  } = useActionHook(getBulkUploadBatchReviewAction, {
    defaultLoadingState: true,
    onReject: (error) => toastify({ message: error?.message || "Failed to load the review batch.", type: "error" })
  });

  const refetch = useCallback(() => {
    if (batchId) loadReview(batchId);
  }, [batchId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const [rows, setRows] = useState<IBulkFileRow[]>([]);

  useEffect(() => {
    setRows(payload?.files.map(toFileRow) ?? []);
  }, [payload]);

  const updateFileRow = useCallback<UseBulkReviewState["updateFileRow"]>((fileId, patch) => {
    setRows((prev) => prev.map((row) => (row.fileId === fileId ? { ...row, ...patch } : row)));
  }, []);

  const valueRows = useMemo(() => payload?.files.flatMap(toValueRows) ?? [], [payload]);

  const measureColumns = useMemo(() => {
    const seen: string[] = [];
    payload?.files.forEach((file) =>
      file.tags.forEach((tag) => {
        const name = tag.measure?.name;
        if (name && !seen.includes(name)) seen.push(name);
      })
    );
    return seen;
  }, [payload]);

  return useMemo(
    () => ({
      loading,
      review: payload,
      rows,
      valueRows,
      measureColumns,
      readOnly: Boolean(payload?.read_only),
      updateFileRow,
      refetch
    }),
    [loading, payload, rows, valueRows, measureColumns, updateFileRow, refetch]
  );
};
